import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListHubspotRecordsInput } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the records of HubSpot as party keeps them (50-api/party.md §2.10, ADR-116), and of its full
// readings that wait for someone (ADR-191). A record that waits enters by itself when what it waits for arrives, but a
// company proposed as a customer, which someone confirms.

const records = api.party["hubspot-records"];
const readings = api.party["hubspot-readings"];

type ListPage = InferResponseType<typeof records.$get, 200>;
export type HubspotRecord = ListPage["items"][number];
export type HubspotReading = InferResponseType<typeof readings.$get, 200>["items"][number];

/** The filters of the list input (`listHubspotRecordsInput`), without the paging the query adds. */
export type HubspotRecordFilters = Pick<ListHubspotRecordsInput, "kind" | "waitingReason" | "q"> & {
  waiting?: boolean | undefined;
  archived?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "hubspot-records"] as const,
  lists: [...partyQueries.all, "hubspot-records", "list"] as const,
  waitingReadings: [...partyQueries.all, "hubspot-readings", "waiting"] as const,
};

export const hubspotRecordQueries = {
  ...keys,

  list: (filters: HubspotRecordFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          records.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The full readings that wait for someone, the oldest first: few, one page. */
  waitingReadings: () =>
    queryOptions({
      queryKey: keys.waitingReadings,
      queryFn: ({ signal }) =>
        unwrap(readings.$get({ query: { waiting: "true", pageSize: "100" } }, { init: { signal } })),
    }),
};
